import React, {useEffect, useRef, useState} from 'react';

import ReactHtmlParser from 'react-html-parser';
import {ButtonDropdown, DropdownMenu, DropdownToggle} from 'reactstrap';
import styled from 'styled-components';

import {LazyTooltip} from 'app/components/general/LazyTooltip';
import {ContentEditable} from 'app/components/general/Mentionable';
import {ViewModeTitle} from 'app/general/detailpage/ViewModeTitle';
import StateButton from 'app/general/widgets/StateButton';
import {useWsOpenaiMessage} from 'app/openai/hooks';
import {useHistoryData} from 'app/openai/utils';
import {gt} from 'app/utils/i18n';

import {
  ChatMessage,
  type ChatMessageType,
  TypingIndicator,
} from './ChatMessage';

import type {AIPromptExampleType} from 'app/openai/types';
import type {ChatTypes} from 'app/subscriptions/hooks';

interface MessagePanelProps {
  className?: string;
  messageHistory: Array<ChatMessageType>;
  chatMessage: string;
  chatStatus: 'ready' | 'sending' | 'processing' | 'error' | 'streaming';
  submitMessage: () => void;
  setChatMessage: (message: string) => void;
  toggleMessagePanel?: () => void;
  prompts?: Array<AIPromptExampleType>;
  title?: string;
  disabled: boolean;
}

export const MessagePanel = ({
  className,
  messageHistory,
  chatMessage,
  chatStatus,
  submitMessage,
  setChatMessage,
  toggleMessagePanel,
  prompts,
  title,
  disabled,
}: MessagePanelProps) => {
  const ref = useRef<HTMLInputElement | null>(null);

  const onSubmit = (e) => {
    e.preventDefault();
    submitMessage();
  };

  useEffect(() => {
    if (
      ref.current &&
      messageHistory.length > 1 &&
      chatStatus !== 'processing' &&
      chatStatus !== 'sending' &&
      chatStatus !== 'streaming'
    ) {
      ref.current.focus();
    }
  }, [messageHistory, chatStatus]);

  return (
    <div
      className={className != null ? className : 'col-md-6'}
      style={{zIndex: 0}}
    >
      <ViewModeTitle
        title={title}
        actionsBox={
          <>
            <AIPrompts
              prompts={prompts}
              onClick={(prompt) => setChatMessage(prompt.prompt)}
            />
            {toggleMessagePanel ? (
              <div className="btn-group title-buttons d-print-none">
                <LazyTooltip tooltip={gt('Hide SweetAI')}>
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={toggleMessagePanel}
                  >
                    <i className="fas fa-fw fa-chevron-right" />
                  </button>
                </LazyTooltip>
              </div>
            ) : null}
          </>
        }
      />
      <div className="d-flex flex-column vh-100 pre-scrollable">
        <div className="flex-grow-1 overflow-auto d-flex flex-column-reverse px-1">
          {chatStatus === 'processing' || chatStatus === 'sending' ? (
            <ChatMessage message={{source: 'ai', text: <TypingIndicator />}} />
          ) : null}
          {messageHistory.map((message, iteration) => (
            <ChatMessage
              key={`chat-message-${iteration}`}
              message={{...message, text: ReactHtmlParser(message.text)}}
            />
          ))}
        </div>

        <div className="input-group">
          <ContentEditable
            className="form-control overflow-auto h-auto"
            disabled={
              chatStatus === 'processing' ||
              chatStatus === 'sending' ||
              chatStatus === 'streaming' ||
              disabled
            }
            onSubmit={onSubmit}
            value={chatMessage}
            style={{
              maxHeight: '8em',
            }}
            onChange={setChatMessage}
            ref={ref}
          />
          <div className="input-group-append">
            <StateButton
              className="btn rounded-0 btn-primary shadow-none"
              busyContent={<i className="far fa-exclamation-circle"></i>}
              disabled={
                chatStatus === 'processing' ||
                chatStatus === 'sending' ||
                chatStatus === 'streaming' ||
                disabled
              }
              isIdle={true}
              onClick={onSubmit}
            >
              <i className="fa fa-paper-plane"></i>
            </StateButton>
          </div>
        </div>
      </div>
    </div>
  );
};

interface AIPromptsProps {
  prompts?: Array<AIPromptExampleType>;
  onClick: (prompt: AIPromptExampleType) => unknown;
}

function AIPrompts({prompts, onClick}: AIPromptsProps) {
  const [isOpen, setIsOpen] = useState<boolean>(false);

  if (prompts == null || prompts.length === 0) {
    return null;
  }
  return (
    <ButtonDropdown isOpen={isOpen} toggle={() => setIsOpen((open) => !open)}>
      <DropdownToggle
        caret
        size="sm"
        color="prim"
        className="btn-text border-right pr-2"
      >
        {gt('Prompts')}
      </DropdownToggle>
      <DropdownMenu>
        <StyledDropdownMenu>
          {prompts.map((prompt) => (
            <a
              key={prompt.name}
              className="list-group-item list-group-item-action text-truncate"
              href="#"
              onClick={() => {
                onClick(prompt);
                setIsOpen(false);
              }}
            >
              <h5 className="mb-1">{prompt.name}</h5>
              <small className="mb-1 text-muted">{prompt.prompt}</small>
            </a>
          ))}
        </StyledDropdownMenu>
      </DropdownMenu>
    </ButtonDropdown>
  );
}

const StyledDropdownMenu = styled.div`
  width: 500px;
  h5 {
    text-transform: none;
    font-weight: normal;
  }
`;

type MessageExchangeProps = {
  threadId: string;
  chatType: ChatTypes;
  object_id?: number;
  content_type?: string;
  disabled?: boolean;
  initialAIMessage?: string;
  placeholderMessage?: string;
  toggleMessagePanel?: () => void;
  prompts?: Array<AIPromptExampleType>;
};
export const MessageExchange = ({
  threadId,
  chatType,
  object_id,
  content_type,
  toggleMessagePanel,
  prompts,
  disabled = false,
  initialAIMessage,
  placeholderMessage = '',
}: MessageExchangeProps) => {
  const [messageHistory, appendMessageHistory, setCurrentMessage] =
    useHistoryData<ChatMessageType>();
  const [chatMessage, setChatMessage] = useState<string>(placeholderMessage);
  const [chatStatus, setChatStatus] = useState<
    'ready' | 'sending' | 'processing' | 'error' | 'streaming'
  >('ready');

  useEffect(() => {
    if (initialAIMessage != null) {
      appendMessageHistory({
        source: 'ai',
        text: initialAIMessage,
      });
    }
  }, []);

  const submitMessage = () => {
    sendMessage(chatMessage);
    setChatStatus('sending');
    appendMessageHistory({source: 'me', text: chatMessage});
  };

  const {sendMessage, message, isLoading, isError, error, isOpen} =
    useWsOpenaiMessage({
      type: chatType,
      object_id,
      content_type,
      thread_id: threadId,
    });

  useEffect(() => {
    if (
      (chatStatus === 'sending' ||
        chatStatus === 'processing' ||
        chatStatus === 'streaming') &&
      error
    ) {
      appendMessageHistory({
        source: 'error',
        text: error,
      });
      setChatStatus('error');
    }
  }, [isError]);

  useEffect(() => {
    if (isLoading) {
      setChatStatus('processing');
    } else {
      setChatStatus('ready');
    }
  }, [isLoading]);

  useEffect(() => {
    if (message) {
      setCurrentMessage({
        source: 'ai',
        text: message,
      });
      setChatMessage('');
      setChatStatus('streaming');
    }
  }, [message]);

  return (
    <MessagePanel
      className="mt-0 mb-4"
      messageHistory={messageHistory}
      chatMessage={chatMessage}
      chatStatus={chatStatus}
      setChatMessage={setChatMessage}
      title={gt('Sweet AI')}
      submitMessage={submitMessage}
      toggleMessagePanel={toggleMessagePanel}
      prompts={prompts}
      disabled={!isOpen || disabled}
    />
  );
};
